import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, Loader2, X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import { Button } from "../../primitives/button";
import { ActionStatusBadge } from "../action-card/ActionStatusBadge";
import type { ActionDrawerStatus } from "../action-drawer/types";
import { ActionKeyboardHints, ActionUndoBar } from "./action-lifecycle";

/**
 * ActionProposalSurface — the single, generic "an action proposal TAKES
 * OVER the work area" shell.
 *
 * Extracted from ``SlideDeckProposalSurface`` (Wave 9) so EVERY action
 * type — presentation deck, email, calendar event, Slack/Teams message,
 * Salesforce / Fatture in Cloud, document edit/create, … — surfaces the
 * SAME chrome instead of a small disconnected banner the user has to
 * click into:
 *
 *   - a sticky toolbar row: optional tab strip (``tabs``) + rationale
 *     eyebrow + status badge + the in-surface Rifiuta / Conferma decision
 *     bar (the decision lives INSIDE the surface — the proposal and its
 *     editor are ONE thing);
 *   - an optional editable header row (icon badge + filename / title
 *     input) — read-only when ``onTitleChange`` is omitted;
 *   - a scrollable ``body`` slot — THIS is what each action type swaps:
 *       · presentation → ``SlideOutlineEditor`` + graphical preview
 *       · email / event / SF / FiC / message → the schema-driven
 *         ``ActionFieldEditor`` (or its vendor preview wrapper).
 *
 * Pure presentational — no store, no router, no fetcher. The container
 * (apps layer) owns the draft state, the execute transport, and the
 * in-place refresh-on-executed, exactly as ``SlideDeckProposalMount``
 * already does. ``busy`` (execute in flight) disables every interactive
 * control; ``status === "executed"`` swaps the decision bar for a quiet
 * confirmation so the surface reads as "done" without yanking the user.
 *
 * Brand: cream surface, orange-accent active tab + primary Confirm CTA,
 * hairline borders, lucide 1.75–2 stroke.
 */

export interface ActionProposalSurfaceTab {
  readonly key: string;
  readonly label: string;
  readonly Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

/**
 * Pending pre-execute "Annulla invio" window (Feature 2). When set, the
 * decision bar is REPLACED by the ``ActionUndoBar`` countdown — the actual
 * defer/cancel timer lives in the apps-layer ``useConfirmWithUndo`` hook.
 */
export interface ActionSurfacePending {
  readonly secondsLeft: number;
  readonly totalSeconds: number;
  readonly onCancel: () => void;
  /** Verb for the countdown ("Invio" / "Pagamento" / …). */
  readonly actionNoun?: string;
}

export interface ActionProposalSurfaceProps {
  /**
   * Optional tab strip. When omitted (most scalar actions), the toolbar
   * shows only the rationale eyebrow + decision bar. When provided, the
   * caller fully controls the active tab (``activeTab`` / ``onTabChange``)
   * and renders the matching ``body`` itself — the shell only paints the
   * tab buttons.
   */
  readonly tabs?: ReadonlyArray<ActionProposalSurfaceTab>;
  readonly activeTab?: string;
  readonly onTabChange?: (next: string) => void;
  readonly tablistLabel?: string;
  /**
   * Optional editable header row. Pass ``icon`` + ``title`` to render the
   * filename/title strip; supply ``onTitleChange`` to make it an editable
   * input (presentation filename), omit it for a read-only label
   * (vendor entity name). When ``title`` is absent the row is hidden and
   * the body fills the surface directly.
   */
  readonly icon?: React.ReactNode;
  readonly title?: string;
  readonly onTitleChange?: (next: string) => void;
  readonly titlePlaceholder?: string;
  readonly titleAriaLabel?: string;
  /** Eyebrow rationale shown next to the tabs (truncated). */
  readonly rationale?: string;
  /**
   * Live lifecycle status — drives the status badge + the executed-state
   * decision-bar swap. Defaults to ``proposed`` so a caller that only
   * tracks ``busy`` still renders sensibly.
   */
  readonly status?: ActionDrawerStatus;
  /** Spinner on Conferma + every interactive element disabled. */
  readonly busy?: boolean;
  /** Disable Conferma without the busy spinner (e.g. validation errors). */
  readonly confirmDisabled?: boolean;
  /** Confirm CTA — caller awaits the BE execute/finalize mutation. */
  readonly onConfirm: () => void;
  /** Reject CTA — caller awaits the BE reject (or removes the proposal). */
  readonly onReject?: () => void;
  /** Confirm CTA label — defaults to "Conferma". */
  readonly confirmLabel?: string;
  readonly confirmAriaLabel?: string;
  /**
   * Optional controls injected into the toolbar, just LEFT of the Rifiuta /
   * Conferma decision bar (e.g. a presence stack, a "next in queue" fast-path,
   * an "open in chat" affordance). Lets a host keep ONE toolbar instead of
   * stacking a second header row of its own. Omitted → nothing extra.
   */
  readonly headerActions?: React.ReactNode;
  /** The action-type-specific editor / preview. */
  readonly body: React.ReactNode;
  /**
   * Pre-execute undo window (Feature 2). When present, the decision bar is
   * swapped for the "Annulla invio" countdown until it elapses or is cancelled.
   */
  readonly pending?: ActionSurfacePending;
  /**
   * Post-execute RECEIPT row (Feature 3) — a deep link to the artefact. Shown
   * under the toolbar once ``status === "executed"``. Reuse ``ActionReceipt``.
   */
  readonly receipt?: React.ReactNode;
  /**
   * Inline error-recovery banner (Feature 4) — cause-mapped CTA shown at the
   * top of the body when the last execute failed. Reuse ``ActionErrorRecovery``.
   */
  readonly errorBanner?: React.ReactNode;
  /**
   * Show the ``⌘↵ Conferma · Esc Rifiuta`` affordance hints under the decision
   * bar. Defaults to ``true``; keyboard shortcuts are always wired regardless.
   */
  readonly showKeyboardHints?: boolean;
  readonly className?: string;
}

export const ActionProposalSurface: React.FC<ActionProposalSurfaceProps> = ({
  tabs,
  activeTab,
  onTabChange,
  tablistLabel,
  icon,
  title,
  onTitleChange,
  titlePlaceholder,
  titleAriaLabel,
  rationale,
  status = "proposed",
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onReject,
  confirmLabel,
  confirmAriaLabel,
  headerActions,
  body,
  pending,
  receipt,
  errorBanner,
  showKeyboardHints = true,
  className,
}) => {
  const { i18n } = useLingui();
  const resolvedTablistLabel = tablistLabel ?? i18n._(msg`Modalità proposta`);
  const resolvedTitlePlaceholder = titlePlaceholder ?? i18n._(msg`Titolo`);
  const resolvedTitleAriaLabel = titleAriaLabel ?? i18n._(msg`Titolo`);
  const resolvedConfirmLabel = confirmLabel ?? i18n._(msg`Conferma`);
  const resolvedConfirmAriaLabel = confirmAriaLabel ?? i18n._(msg`Conferma la proposta`);
  const executed = status === "executed";
  const hasHeaderRow = title !== undefined;
  const isPending = pending !== undefined;

  // ── Keyboard-first (Feature 1) ──────────────────────────────────────────
  // Scoped to the card: the handler sits on the surface ROOT, so it only fires
  // when focus is somewhere inside the card (a field, a button, the container)
  // — it never hijacks global page shortcuts. ⌘/Ctrl+Enter confirms; Esc
  // cancels a pending send first, otherwise rejects.
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        if (isPending) {
          e.preventDefault();
          pending?.onCancel();
          return;
        }
        if (onReject && !busy && !executed) {
          e.preventDefault();
          onReject();
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (!busy && !confirmDisabled && !executed && !isPending) {
          e.preventDefault();
          onConfirm();
        }
      }
    },
    [isPending, pending, onReject, onConfirm, busy, confirmDisabled, executed],
  );

  return (
    <div
      className={cn("flex h-full w-full flex-col bg-cream", className)}
      onKeyDown={handleKeyDown}
    >
      {/* Tab strip + rationale + status + decision CTAs — single toolbar row. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-hairline border-b bg-cream/95 px-3 py-1.5 backdrop-blur">
        {tabs && tabs.length > 0 ? (
          <div role="tablist" aria-label={resolvedTablistLabel} className="flex items-center gap-1">
            {tabs.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTab === key}
                onClick={() => onTabChange?.(key)}
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 font-medium text-[11.5px] transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                  activeTab === key
                    ? "bg-ando-orange-soft text-ando-orange"
                    : "text-ink-2 hover:bg-ink-1/[0.04] hover:text-ink-1",
                )}
              >
                <Icon className="h-3 w-3" aria-hidden strokeWidth={2} />
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {rationale ? (
          <p
            className="hidden min-w-0 max-w-[40ch] flex-1 truncate text-[11.5px] text-ink-2 md:block"
            title={rationale}
          >
            {rationale}
          </p>
        ) : null}

        {headerActions ? (
          <div className="ml-auto flex items-center gap-1.5">{headerActions}</div>
        ) : null}

        <div className="ml-auto flex flex-col items-end gap-1">
          {executed ? (
            <span className="inline-flex items-center gap-1.5 font-medium text-[12px] text-success-foreground">
              <Check className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
              {i18n._(msg`Eseguita`)}
            </span>
          ) : isPending && pending ? (
            // Undo window (Feature 2) — the decision bar is swapped for the
            // "Annulla invio" countdown; nothing has been sent yet.
            <ActionUndoBar
              secondsLeft={pending.secondsLeft}
              totalSeconds={pending.totalSeconds}
              onCancel={pending.onCancel}
              actionNoun={pending.actionNoun}
            />
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <ActionStatusBadge status={status} className="hidden sm:inline-flex" />
                {onReject ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onReject}
                    disabled={busy}
                    aria-label={i18n._(msg`Rifiuta la proposta`)}
                  >
                    <X className="h-3 w-3" aria-hidden strokeWidth={1.75} />
                    {i18n._(msg`Rifiuta`)}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={onConfirm}
                  disabled={busy || confirmDisabled}
                  aria-label={resolvedConfirmAriaLabel}
                  aria-busy={busy || undefined}
                  className="min-w-[96px] justify-center"
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden strokeWidth={2} />
                  ) : (
                    <Check className="h-3 w-3" aria-hidden strokeWidth={2} />
                  )}
                  {resolvedConfirmLabel}
                </Button>
              </div>
              {showKeyboardHints && !busy ? (
                <ActionKeyboardHints canReject={onReject !== undefined} />
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Post-execute RECEIPT (Feature 3) — deep link to the artefact. */}
      {executed && receipt ? receipt : null}

      {/* Header row — editable filename / read-only title, mirrors
          DocumentCreatePreview's header. Hidden when no title supplied. */}
      {hasHeaderRow ? (
        <div className="flex shrink-0 items-center gap-2 border-hairline border-b bg-white/60 px-3 py-2">
          {icon ? (
            <span
              aria-hidden
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-ando-orange-soft text-ando-orange"
            >
              {icon}
            </span>
          ) : null}
          {onTitleChange ? (
            <input
              type="text"
              aria-label={resolvedTitleAriaLabel}
              className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 font-medium text-[13px] text-ink-1 hover:border-hairline focus:border-ando-orange focus:bg-white focus:outline-none focus:ring-2 focus:ring-ando-orange disabled:opacity-50"
              value={title}
              placeholder={resolvedTitlePlaceholder}
              disabled={busy}
              onChange={(e) => onTitleChange(e.target.value)}
            />
          ) : (
            <span className="min-w-0 flex-1 truncate font-medium text-[13px] text-ink-1">
              {title}
            </span>
          )}
        </div>
      ) : null}

      {/* Body — the action-type-specific editor / preview. The inline
          error-recovery banner (Feature 4) leads the body when present. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {errorBanner ? <div className="px-3 pt-3">{errorBanner}</div> : null}
        {body}
      </div>
    </div>
  );
};
ActionProposalSurface.displayName = "ActionProposalSurface";
